import {
  ReviewsAPIResponse,
  TBlogPost,
  TBlogPostResult,
  TFAQList,
  THubSpotCTA,
  TReviews,
} from './cms-types'
import type { HubspotctaDocument } from '../prismicio-types'
import { slimDocument, slimSlices } from './cms-helpers'
import { retiredBlogSlugs } from './utils/retiredBlogSlugs'
import dayjs from 'dayjs'
import { BuildQueryURLArgs, Client, predicate } from '@prismicio/client'
import { PrismicDocument, Query } from '@prismicio/types'
import fetch from 'node-fetch'
import { Utils } from './utils'

export const transformBlogPost = function (post: TBlogPost) {
  const res: TBlogPost = {
    ...post,
    data: {
      ...post.data,
      modified_date_iso: post.last_publication_date,
      published_date: post.data.published_date
        ? dayjs(post.data.published_date).format('DD MMMM, YYYY')
        : null,
      published_date_iso: post.first_publication_date,
    },
  }
  return res
}

export const transformBlogPostResult = function (post: TBlogPost) {
  const res: TBlogPostResult = slimDocument({
    ...post,
    data: {
      body_excerpt: post.data.body_excerpt,
      main_image: post.data.main_image,
      modified_date: dayjs(post.data.published_date).format('DD MMMM, YYYY'),
      title: post.data.title,
    },
  })
  return res
}

export const getPosts = (
  client: Client<PrismicDocument>,
  page = 1,
  lang: string,
  pageSize: number,
  existingPosts?: Query<TBlogPostResult>,
  tag?: string,
  search?: string,
) => {
  // Posts whose URL 301s elsewhere are still published in Prismic, so Prismic
  // keeps returning them. Excluding them in the query (rather than after the
  // fact) keeps page sizes and total_results_size correct for pagination.
  const excludeRetired = retiredBlogSlugs.map((uid) =>
    predicate.not('my.blog_post.uid', uid),
  )
  const params: Partial<BuildQueryURLArgs> = {
    lang,
    orderings: 'my.blog_post.published_date desc',
    page,
    pageSize,
    predicates: search
      ? [predicate.fulltext('my.blog_post.body', search), ...excludeRetired]
      : excludeRetired,
  }
  return (
    tag
      ? client.getByTag(tag, params)
      : client.getByType<TBlogPost>('blog_post', params)
  ).then((res) => {
    return {
      ...res,
      results: (existingPosts?.results || []).concat(
        res.results.map((post) => {
          return transformBlogPostResult(post as TBlogPost)
        }),
      ),
    }
  })
}

export const getTags = (client: Client<PrismicDocument>, lang: string) => {
  return client.getTags({}).then((res) => {
    return res
  })
}

export const getHubSpotCTAs = (
  client: Client<PrismicDocument>,
  lang: string,
): Promise<THubSpotCTA[]> => {
  return client
    .getAllByType<HubspotctaDocument>('hubspotcta', { lang: '*' })
    .then((res) => {
      const seen = new Set<string>()
      return res
        .filter((doc) => {
          if (seen.has(doc.uid)) return false
          seen.add(doc.uid)
          return true
        })
        .map((doc) => ({
          ctaId: doc.uid,
          href: doc.data.href || '',
          id: doc.id,
          imgSrc: doc.data.imgsrc || '',
          tags: doc.data.tags || '',
        }))
    })
}

// Decide which HubSpot CTAs a blog post should render.
// 1. An explicit CTA chosen on the post always wins.
// 2. Otherwise the first CTA whose tags CSV matches a post tag is forced.
// 3. No explicit CTA and no tag match -> whole list (random pick is client-side).
export const resolveBlogPostCTAs = (
  ctas: THubSpotCTA[],
  postTags: string[] = [],
  linkedCtaId?: string,
): THubSpotCTA[] => {
  if (linkedCtaId) {
    const linked = ctas.find((cta) => cta.id === linkedCtaId)
    if (linked) return [linked]
  }
  const normalisedTags = postTags.map((tag) => tag.trim().toLowerCase())
  const tagMatch = ctas.find((cta) =>
    cta.tags
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean)
      .some((tag) => normalisedTags.includes(tag)),
  )
  if (tagMatch) return [tagMatch]
  return ctas
}

export const getFAQList = (
  client: Client<PrismicDocument>,
  lang: string,
  uid: string,
) => {
  return client.getByUID<TFAQList>('faq_list', uid).then((res) => {
    return slimDocument({
      ...res,
      data: {
        ...res.data,
        slices: slimSlices(res.data.slices as any),
      },
    })
  })
}

export const getReviews = async () => {
  const url = `https://api.reviews.io/reviews?store=hoxton-mix&minRating=4&per_page=6&page=1&include_product_reviews=true&include_local_reviews=true&apikey=${process.env.REVIEWS_API_KEY}`

  const res: ReviewsAPIResponse = await fetch(url).then((res) => res.json())
  const worstToBest = Object.keys(res.stats.ratings).sort(
    (a, b) => parseInt(a) - parseInt(b),
  )
  return {
    average: parseFloat(res.average_rating),
    best: parseInt(worstToBest[worstToBest.length - 1]),
    numberOfReviews: res.review_count,
    reviews: Utils.parseReviews(res.reviews),
    url,
    worst: parseInt(worstToBest[0]),
  } as TReviews
}
